/**
 * The cleaning board's filter state, and the query string it lives in (CAP-412).
 *
 * **The URL is the state, not a copy of it.** Every control writes here and
 * every read comes back from here, so a filtered view is a link: paste it into
 * a message, reopen it tomorrow, and it is the same view. A second copy in
 * React state would be a second source of truth for the same fact, and the two
 * drift the first time someone uses the back button.
 *
 * Pure and free of React on purpose, so the round trip - state to query string
 * and back - can be exercised without rendering anything.
 *
 * What is deliberately NOT in here: how far the list has been scrolled. That is
 * a property of this visit rather than of the view, and writing it to the URL
 * would put a history entry behind every batch and make Back mean "up 50 rows".
 */

import {
  CLEANING_ACTION_KINDS,
  CLEANING_UNSET_FILTER,
  type CleaningActionKind,
  type CleaningJob,
} from '@/api/client'

export type CleaningTab = 'actions' | 'all'

/** The substates the All-cleanings tab offers as a filter. */
export const CLEANING_SUBSTATES = [
  'needs_decision',
  'tentative',
  'needs_team',
  'needs_ack',
  'ready',
] as const
export type CleaningSubstate = NonNullable<CleaningJob['substate']>

export interface CleaningFilters {
  tab: CleaningTab
  /** A property id, or '' for all. */
  property: string
  /** A crew id, `none` for the unassigned, or '' for any. */
  crew: string
  /** Action kinds; empty means every kind. Actions tab only. */
  kinds: CleaningActionKind[]
  /** Job substates; empty means every substate. All-cleanings tab only. */
  substates: CleaningSubstate[]
}

/**
 * Where an unfiltered board starts.
 *
 * There is no time-window filter: the old When control (CAP-412) was dropped
 * as not useful, and a stale `?window=` link is ignored rather than applied
 * invisibly.
 */
export const DEFAULT_CLEANING_FILTERS: CleaningFilters = {
  tab: 'all',
  property: '',
  crew: '',
  kinds: [],
  substates: [],
}

function readList<T extends string>(
  params: URLSearchParams,
  name: string,
  allowed: readonly T[]
): T[] {
  const raw = params.getAll(name)
  if (raw.length === 0) return []
  const known = new Set<string>(allowed)
  const seen = new Set<T>()
  for (const entry of raw.flatMap(value => value.split(','))) {
    const trimmed = entry.trim()
    // An unknown value is dropped rather than rejected. A hand-edited or
    // stale link should still open on something sensible - refusing to render
    // because one of five kinds was renamed is worse than showing the four
    // that still exist.
    if (known.has(trimmed)) seen.add(trimmed as T)
  }
  // Returned in the canonical order, never the order they appeared in the URL,
  // so two links selecting the same kinds produce the same request and hit the
  // same query cache entry.
  return allowed.filter(value => seen.has(value))
}

/**
 * Filter state from a query string. Anything unrecognised falls back.
 *
 * Only `?tab=actions` opens the actions tab. Everything else, including the
 * pre-CAP-526 `?tab=upcoming` and a bare `/cleaning`, opens All cleanings
 * (CAP-1363).
 *
 * The one exception is a heads-up email's `?job=` link with no `?tab=`: it
 * names a row on the actions table, those links are already out in inboxes and
 * cannot be rewritten, and landing them on All cleanings would ring nothing.
 */
export function readCleaningFilters(params: URLSearchParams): CleaningFilters {
  const tabRaw = params.get('tab')
  const tab: CleaningTab =
    tabRaw === 'actions' || (tabRaw === null && params.has('job')) ? 'actions' : 'all'
  return {
    tab,
    property: params.get('property') ?? '',
    crew: params.get('crew') ?? '',
    kinds: readList(params, 'kind', CLEANING_ACTION_KINDS),
    substates: readList(params, 'state', CLEANING_SUBSTATES),
  }
}

/**
 * Filter state back into a query string.
 *
 * Defaults are omitted, so an unfiltered board has a clean `/cleaning` URL and
 * two links describing the same view are character-for-character identical.
 * A filter belonging to the other tab is dropped rather than carried along
 * invisibly - `kind` means nothing on All cleanings, and a hidden filter that
 * reappears when you switch back is the kind of thing nobody debugs.
 */
export function writeCleaningFilters(filters: CleaningFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.tab !== DEFAULT_CLEANING_FILTERS.tab) params.set('tab', filters.tab)
  if (filters.property) params.set('property', filters.property)
  if (filters.crew) params.set('crew', filters.crew)
  if (filters.tab === 'actions' && filters.kinds.length > 0) {
    params.set('kind', filters.kinds.join(','))
  }
  if (filters.tab === 'all' && filters.substates.length > 0) {
    params.set('state', filters.substates.join(','))
  }
  return params
}

/** Whether anything is narrowed, which is what enables "Clear filters". */
export function hasActiveCleaningFilters(filters: CleaningFilters): boolean {
  const relevant = filters.tab === 'actions' ? filters.kinds.length : filters.substates.length
  return filters.property !== '' || filters.crew !== '' || relevant > 0
}

/** Clear everything except which tab is open. */
export function clearCleaningFilters(filters: CleaningFilters): CleaningFilters {
  return { ...DEFAULT_CLEANING_FILTERS, tab: filters.tab }
}

/** Add or remove one value from a multi-select, keeping the canonical order. */
export function toggleInList<T extends string>(
  current: readonly T[],
  value: T,
  allowed: readonly T[]
): T[] {
  const next = new Set<T>(current)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return allowed.filter(entry => next.has(entry))
}

/** The crew filter's sentinel, re-exported so callers need one import. */
export const CREW_UNASSIGNED = CLEANING_UNSET_FILTER
